import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import AdminHeader from './AdminHeader';
import Pagination from '../common/Pagination';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { AllBusiness } from '../../services/api';
const ITEMS_PER_PAGE = 10;

const AdminUserManagement = ( showBack = true) => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('displayName');
  const [sortDir, setSortDir] = useState('asc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Prefer API
        try {
          const resp = await AllBusiness({});
          const list = Array.isArray(resp.extractedData) ? resp.extractedData : (resp.extractedData?.records || []);
          const rows = list.map((u, idx) => ({
            id: u.profile_id || u.id || String(idx),
            email: u.email || u.email_id || '',
            role: u.role || u.registrationType || 'customer',
            displayName: u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : (u.displayName || u.businessName || u.name || ''),
            createdAt: u.created_on ? new Date(u.created_on) : null,
          }));
          setUsers(rows);
          setFiltered(rows);
        } catch (apiErr) {
          // Fallback to Firestore
          const snap = await getDocs(collection(db, 'users'));
          const rows = [];
          snap.forEach((d) => {
            const data = d.data();
            rows.push({
              id: d.id,
              email: data.email,
              role: data.role || 'customer',
              displayName: data.displayName || '',
              createdAt: data.createdAt ? (typeof data.createdAt?.toDate === 'function' ? data.createdAt.toDate() : new Date(data.createdAt)) : null,
            });
          });
          setUsers(rows);
          setFiltered(rows);
        }
      } catch (e) {
        const rows = [
          { id: '1', email: 'customer1@test.com', role: 'customer', displayName: 'John Doe', createdAt: new Date() },
          { id: '2', email: 'business1@test.com', role: 'business', displayName: 'ABC Store', createdAt: new Date(Date.now() - 86400000) },
        ];
        setUsers(rows);
        setFiltered(rows);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleUserRoleUpdate = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), { role: newRole });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)));
    } catch (e) {
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await deleteDoc(doc(db, 'users', userId));
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (e) {
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    }
  };
  const handleBack = () => {
    // if (onBack) return onBack();
    navigate('/admin/dashboard');
  };

  useEffect(() => {
    let data = [...users];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter((u) => (u.displayName || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      data = data.filter((u) => !u.createdAt || u.createdAt >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      data = data.filter((u) => !u.createdAt || u.createdAt <= to);
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    data.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      if (av instanceof Date) return ((av) - (bv)) * dir;
      return (Number(av) - Number(bv)) * dir;
    });
    setFiltered(data);
    setCurrentPage(1);
  }, [search, dateFrom, dateTo, sortBy, sortDir, users]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
  };

  const { pageItems, totalPages } = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return {
      pageItems: filtered.slice(start, end),
      totalPages: Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE)),
    };
  }, [filtered, currentPage]);

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader   />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div>
        {showBack && (
              <button
                onClick={handleBack}
                className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-black px-3 py-2 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
            )}
        </div>
        <p className="text-2xl font-bold text-gray-900">User Management</p>
        <div className="bg-white rounded-lg shadow p-4 mb-6">
       
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or email..."
              className="border border-gray-300 rounded-lg px-3 py-2"
            />
            <div>
              <label className="block text-xs text-gray-600 mb-1">From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">To</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <button onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); }} className="self-end bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">Clear</button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th onClick={()=>toggleSort('displayName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">User {sortBy==='displayName' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={()=>toggleSort('email')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Email {sortBy==='email' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={()=>toggleSort('role')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Role {sortBy==='role' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={()=>toggleSort('createdAt')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Created {sortBy==='createdAt' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td className="px-6 py-8 text-center text-gray-500" colSpan={5}>Loading...</td></tr>
                ) : pageItems.length === 0 ? (
                  <tr><td className="px-6 py-8 text-center text-gray-500" colSpan={5}>No users found</td></tr>
                ) : (
                  pageItems.map((u) => (
                    <tr key={u.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.displayName || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <select
                          value={u.role}
                          onChange={(e) => handleUserRoleUpdate(u.id, e.target.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="customer">Customer</option>
                          <option value="business">Business</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.createdAt ? u.createdAt.toLocaleDateString() : '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button onClick={() => handleDeleteUser(u.id)} className="text-red-600 hover:text-red-900">Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.max(1, totalPages)}
              onPageChange={(p) => setCurrentPage(Math.max(1, Math.min(p, totalPages)))}
              itemsPerPage={ITEMS_PER_PAGE}
              totalItems={filtered.length}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserManagement;
